import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ArrowRight, FileText, Save } from 'lucide-react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { LoadError } from '../../../components/layout/LoadError';
import { EmptyState } from '../../../components/ui/EmptyState';
import { ConsequenceBody, useConsequence } from '../../../components/ui/ConsequencePreview';
import { Stepper, type FlowStep } from '../../../components/ui/GuidedFlow';
import { useToast } from '../../../components/ui/toast';
import {
  useCreateDocKitBatch,
  useCreateDocKitPlan,
  useCreateDocProfileRevision,
  useDocKitCatalog,
  useDocKitProfile,
} from '../../../hooks/doc-kit';
import { ApiError } from '../../../lib/api-client';
import type { AckMode, DocKitPlanResponse, DocKitResponse, DocProfile, DocQuestion } from '@payroll/shared';

const STEPS: FlowStep[] = [
  { id: '1', label: 'ข้อมูลบริษัท' },
  { id: '2', label: 'เวลาทำงาน' },
  { id: '3', label: 'สิทธิ์การลา' },
  { id: '4', label: 'วันหยุด' },
  { id: '5', label: 'ผู้ลงนาม' },
  { id: '6', label: 'สรุปและสร้าง' },
];

const PLAN_STORAGE_KEY = 'paypilot.doc-kit.plan';
const ACK_STORAGE_KEY = 'paypilot.doc-kit.ack-overrides';
const EFFECTIVE_DATE_STORAGE_KEY = 'paypilot.doc-kit.effective-from';

interface StoredPlan {
  selected_codes: string[];
  plan: DocKitPlanResponse;
}

function isUnavailable<T extends object>(data: DocKitResponse<T> | undefined): data is { unavailable: string } {
  return Boolean(data && 'unavailable' in data);
}

function actionError(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function todayBkk(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
}

function readStoredPlan(selectedCodes: string[]): DocKitPlanResponse | null {
  try {
    const raw = sessionStorage.getItem(PLAN_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredPlan;
    return stored.selected_codes.join('|') === selectedCodes.join('|') ? stored.plan : null;
  } catch {
    return null;
  }
}

function savePlan(selectedCodes: string[], plan: DocKitPlanResponse): void {
  try {
    sessionStorage.setItem(PLAN_STORAGE_KEY, JSON.stringify({ selected_codes: selectedCodes, plan } satisfies StoredPlan));
  } catch {
    // The wizard remains usable if private browsing disallows session storage.
  }
}

function readAckOverrides(): Record<string, AckMode> {
  try {
    const raw = sessionStorage.getItem(ACK_STORAGE_KEY);
    return raw ? JSON.parse(raw) as Record<string, AckMode> : {};
  } catch {
    return {};
  }
}

function readEffectiveDate(): string {
  try {
    return sessionStorage.getItem(EFFECTIVE_DATE_STORAGE_KEY) ?? todayBkk();
  } catch {
    return todayBkk();
  }
}

function writeEffectiveDate(value: string): void {
  try {
    sessionStorage.setItem(EFFECTIVE_DATE_STORAGE_KEY, value);
  } catch {
    // A date can still be used for this submission without session storage.
  }
}

function defaultValue(question: DocQuestion, profile: DocProfile, registry: Record<string, unknown>): unknown {
  if (Object.prototype.hasOwnProperty.call(registry, question.key)) return registry[question.key];
  if (Object.prototype.hasOwnProperty.call(profile.answers, question.key)) return profile.answers[question.key];
  return question.default_rule && Object.prototype.hasOwnProperty.call(question.default_rule, 'value')
    ? question.default_rule.value
    : '';
}

function initialAnswers(questions: DocQuestion[], profile: DocProfile, registry: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(questions.map((question) => [question.key, defaultValue(question, profile, registry)]));
}

function minimumFromHelp(help: string | null | undefined): number | null {
  const match = help?.match(/ไม่น้อยกว่า\s+([0-9]+(?:[,.][0-9]+)?)/);
  return match?.[1] ? Number(match[1].replace(',', '')) : null;
}

function valueText(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function emptyValue(question: DocQuestion, value: unknown): boolean {
  if (question.type === 'multi') return !Array.isArray(value) || value.length === 0;
  if (question.type === 'signer') return !value || typeof value !== 'object' || !String((value as { name?: unknown }).name ?? '').trim();
  return value === '' || value === null || value === undefined;
}

function summaryValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (value && typeof value === 'object') {
    const signer = value as { name?: unknown; title?: unknown };
    return `${valueText(signer.name)} ${valueText(signer.title)}`.trim() || 'ยังไม่ได้ระบุ';
  }
  return valueText(value) || 'ยังไม่ได้ระบุ';
}

function WizardSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" aria-label="กำลังโหลดขั้นตอนจัดชุดเอกสาร">
      <div className="card h-10 bg-gray-100" />
      <div className="card h-72 bg-gray-100" />
    </div>
  );
}

function QuestionField({
  question,
  value,
  fromRegistry,
  disabled,
  onChange,
}: {
  question: DocQuestion;
  value: unknown;
  fromRegistry: boolean;
  disabled: boolean;
  onChange: (value: unknown) => void;
}) {
  const minimum = minimumFromHelp(question.help);
  const numericValue = Number(value);
  const belowMinimum = minimum !== null && value !== '' && Number.isFinite(numericValue) && numericValue < minimum;
  const options = question.options ?? [];
  const inputClass = 'input min-h-11 w-full text-base';

  let control: React.ReactNode;
  if (question.type === 'bool') {
    control = (
      <select className={inputClass} value={value === true ? 'true' : value === false ? 'false' : ''} disabled={disabled} onChange={(event) => onChange(event.target.value === 'true')}>
        <option value="">เลือกคำตอบ</option><option value="true">ใช่</option><option value="false">ไม่ใช่</option>
      </select>
    );
  } else if (question.type === 'select') {
    control = <select className={inputClass} value={valueText(value)} disabled={disabled} onChange={(event) => onChange(event.target.value)}><option value="">เลือกคำตอบ</option>{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>;
  } else if (question.type === 'multi') {
    const selected = Array.isArray(value) ? value.map(String) : [];
    control = (
      <div className="space-y-2 rounded-lg border border-gray-200 p-3">
        {options.map((option) => (
          <label key={option.value} className="flex min-h-11 cursor-pointer items-center gap-3 text-base">
            <input type="checkbox" className="h-5 w-5 accent-brand-600" disabled={disabled} checked={selected.includes(option.value)} onChange={(event) => onChange(event.target.checked ? [...selected, option.value] : selected.filter((item) => item !== option.value))} />
            {option.label}
          </label>
        ))}
      </div>
    );
  } else if (question.type === 'signer') {
    const signer = value && typeof value === 'object' ? value as { name?: unknown; title?: unknown } : {};
    control = (
      <div className="grid gap-3 sm:grid-cols-2">
        <input className={inputClass} placeholder="ชื่อผู้ลงนาม" value={valueText(signer.name)} disabled={disabled} onChange={(event) => onChange({ ...signer, name: event.target.value })} />
        <input className={inputClass} placeholder="ตำแหน่ง" value={valueText(signer.title)} disabled={disabled} onChange={(event) => onChange({ ...signer, title: event.target.value })} />
      </div>
    );
  } else {
    const type = question.type === 'date' ? 'date' : question.type === 'number' || question.type === 'money' ? 'number' : 'text';
    control = <input className={inputClass} type={type} inputMode={question.type === 'money' || question.type === 'number' ? 'decimal' : undefined} value={valueText(value)} disabled={disabled} onChange={(event) => onChange(type === 'number' && event.target.value !== '' ? Number(event.target.value) : event.target.value)} />;
  }

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <div className="flex flex-wrap items-center gap-2">
        <label className="font-medium text-gray-900">{question.label}{question.required && <span className="ml-1 text-red-600">*</span>}</label>
        {fromRegistry && <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">จากระบบ</span>}
      </div>
      {question.help && <p className="text-sm text-gray-600">{question.help}</p>}
      {control}
      {belowMinimum && <p className="flex items-start gap-1.5 text-sm text-amber-800"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />ค่านี้ต่ำกว่าข้อความกำหนดขั้นต่ำ ระบบยังให้ดำเนินการต่อได้</p>}
    </fieldset>
  );
}

export function DocKitWizard() {
  const { step: stepParam } = useParams<{ step: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const consequence = useConsequence();
  const catalogQuery = useDocKitCatalog();
  const profileQuery = useDocKitProfile();
  const planMutation = useCreateDocKitPlan();
  const revisionMutation = useCreateDocProfileRevision();
  const batchMutation = useCreateDocKitBatch();
  const [plan, setPlan] = useState<DocKitPlanResponse | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [effectiveFrom, setEffectiveFrom] = useState(readEffectiveDate);
  const initialized = useRef(false);
  const planRequestFor = useRef<string | null>(null);
  const revisionRef = useRef<number | null>(null);
  const creatingRef = useRef(false);
  const [creating, setCreating] = useState(false);
  const rawStep = Number(stepParam);
  const step = Number.isInteger(rawStep) && rawStep >= 1 && rawStep <= 6 ? rawStep : 1;

  const catalog = !isUnavailable(catalogQuery.data) ? catalogQuery.data : undefined;
  const profileData = !isUnavailable(profileQuery.data) ? profileQuery.data : undefined;
  const profile = profileData?.profile;

  useEffect(() => {
    if (!catalog || !profile || initialized.current) return;
    setAnswers(initialAnswers(catalog.questions, profile, profileData.registry));
    revisionRef.current = profile.revision;
    initialized.current = true;
  }, [catalog, profile, profileData]);

  const selectedCodeKey = profile?.selected_codes.join('|') ?? '';
  useEffect(() => {
    if (!profile || !selectedCodeKey) return;
    const selectedCodes = profile.selected_codes;
    if (planRequestFor.current === selectedCodeKey) return;
    const stored = readStoredPlan(selectedCodes);
    if (stored) {
      setPlan(stored);
      planRequestFor.current = selectedCodeKey;
      return;
    }
    planRequestFor.current = selectedCodeKey;
    let active = true;
    void planMutation.mutateAsync({ selected_codes: selectedCodes }).then((response) => {
      if (!active) return;
      if (isUnavailable(response)) {
        toast.error(response.unavailable);
        return;
      }
      savePlan(selectedCodes, response);
      setPlan(response);
    }).catch((error: unknown) => {
      if (active) toast.error(actionError(error, 'วางแผนคำถามไม่สำเร็จ กรุณาลองใหม่'));
    });
    return () => { active = false; };
  }, [planMutation, profile, selectedCodeKey, toast]);

  const pageQuestions = useMemo(() => !catalog || !plan || step === 6
    ? []
    : catalog.questions.filter((question) => question.page === step && plan.questions_needed.includes(question.key)).sort((a, b) => a.sort - b.sort), [catalog, plan, step]);

  async function refetchAfterStale(): Promise<void> {
    const result = await profileQuery.refetch();
    const data = result.data;
    if (!data || isUnavailable(data) || !data.profile || !catalog) return;
    revisionRef.current = data.profile.revision;
    setAnswers(initialAnswers(catalog.questions, data.profile, data.registry));
  }

  async function persist(targetStep: number): Promise<boolean> {
    if (!profile) return false;
    try {
      const response = await revisionMutation.mutateAsync({
        expected_revision: revisionRef.current ?? profile.revision,
        answers,
        selected_codes: profile.selected_codes,
        wizard_step: targetStep,
      });
      if (isUnavailable(response)) {
        toast.error(response.unavailable);
        return false;
      }
      revisionRef.current = response.profile.revision;
      toast.success('บันทึกข้อมูลขั้นตอนแล้ว');
      return true;
    } catch (error) {
      toast.error(actionError(error, 'บันทึกข้อมูลไม่สำเร็จ กรุณาลองใหม่'));
      if (error instanceof ApiError && error.status === 409) await refetchAfterStale();
      return false;
    }
  }

  function validatePage(): boolean {
    const missing = pageQuestions.find((question) => question.required && emptyValue(question, answers[question.key]));
    if (!missing) return true;
    toast.error(`กรุณากรอก ${missing.label}`);
    return false;
  }

  async function moveTo(nextStep: number) {
    if (nextStep > step && !validatePage()) return;
    if (await persist(nextStep)) navigate(`/hr/doc-kit/wizard/${nextStep}`);
  }

  async function createBatch() {
    if (!profile || creatingRef.current || creating) return;
    if (!effectiveFrom) { toast.error('กรุณาระบุวันที่มีผลบังคับใช้'); return; }
    creatingRef.current = true;
    setCreating(true);
    try {
      if (!(await persist(6))) return;
      const confirmed = await consequence({
        title: 'สร้างชุดเอกสาร',
        intro: 'ระบบจะสร้างร่างเอกสารตามข้อมูลที่สรุปไว้และเริ่มจัดทำเอกสารให้',
        lines: [{ label: 'จำนวนเอกสาร', value: `${profile.selected_codes.length} ฉบับ`, emphasis: true }, { label: 'วันที่มีผล', value: effectiveFrom }],
        effects: ['เอกสารจะอยู่ในสถานะร่างก่อนตรวจและเผยแพร่', 'ยังไม่มีผลบังคับใช้จนกว่าจะเผยแพร่'],
        confirmText: 'สร้างร่างเอกสาร',
      });
      if (!confirmed) return;
      const overrides = readAckOverrides();
      const ackOverrides = Object.fromEntries(profile.selected_codes
        .filter((code) => overrides[code])
        .map((code) => [code, overrides[code]!])) as Record<string, AckMode>;
      const response = await batchMutation.mutateAsync({
        request_key: crypto.randomUUID(),
        selected_codes: profile.selected_codes,
        effective_from: effectiveFrom,
        ...(Object.keys(ackOverrides).length > 0 ? { ack_overrides: ackOverrides } : {}),
      });
      if (isUnavailable(response)) { toast.error(response.unavailable); return; }
      toast.success('สร้างร่างเอกสารแล้ว ระบบกำลังจัดทำเอกสารให้');
      navigate(`/hr/doc-kit/batches/${encodeURIComponent(response.batch.id)}`);
    } catch (error) {
      toast.error(actionError(error, 'สร้างชุดเอกสารไม่สำเร็จ กรุณาลองใหม่'));
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  }

  if (catalogQuery.isLoading || profileQuery.isLoading) {
    return <AdminLayout title="จัดทำชุดเอกสาร"><WizardSkeleton /></AdminLayout>;
  }
  if (catalogQuery.error || profileQuery.error || !catalogQuery.data || !profileQuery.data) {
    return <AdminLayout title="จัดทำชุดเอกสาร"><LoadError error={catalogQuery.error ?? profileQuery.error} onRetry={() => { void catalogQuery.refetch(); void profileQuery.refetch(); }} /></AdminLayout>;
  }
  const unavailableMessage = isUnavailable(catalogQuery.data)
    ? catalogQuery.data.unavailable
    : isUnavailable(profileQuery.data)
      ? profileQuery.data.unavailable
      : null;
  if (unavailableMessage) return <AdminLayout title="จัดทำชุดเอกสาร"><div className="card border-l-4 border-l-amber-400 p-4 text-sm">{unavailableMessage}</div></AdminLayout>;
  if (!catalog || !profileData || !profile) {
    return <AdminLayout title="จัดทำชุดเอกสาร"><EmptyState icon={FileText} title="ยังไม่ได้เลือกรายการเอกสาร" detail="เริ่มจากเลือกเอกสารที่องค์กรต้องจัดทำก่อน" action={{ label: 'เลือกเอกสาร', onClick: () => navigate('/hr/doc-kit/select') }} /></AdminLayout>;
  }
  if (profile.selected_codes.length === 0) {
    return <AdminLayout title="จัดทำชุดเอกสาร"><EmptyState icon={FileText} title="ยังไม่ได้เลือกรายการเอกสาร" detail="เลือกเอกสารอย่างน้อยหนึ่งฉบับก่อนเริ่มตอบข้อมูล" action={{ label: 'เลือกเอกสาร', onClick: () => navigate('/hr/doc-kit/select') }} /></AdminLayout>;
  }
  if (!initialized.current || (!plan && !planMutation.isError)) {
    return <AdminLayout title="จัดทำชุดเอกสาร"><WizardSkeleton /></AdminLayout>;
  }
  if (planMutation.isError || !plan) {
    return <AdminLayout title="จัดทำชุดเอกสาร"><LoadError error={planMutation.error} onRetry={() => { planRequestFor.current = null; planMutation.reset(); }} /></AdminLayout>;
  }

  const selectedTemplates = catalog.templates.filter((template) => profile.selected_codes.includes(template.code));
  const busy = revisionMutation.isPending || batchMutation.isPending || creating;
  return (
    <AdminLayout title="จัดทำชุดเอกสาร" subtitle={`ขั้นตอน ${step} จาก 6`}>
      <div className="max-w-3xl space-y-4 pb-24">
        <section className="card p-4"><Stepper steps={STEPS} current={step - 1} disabled={busy} onStepClick={(index) => { void moveTo(index + 1); }} /></section>
        {step < 6 ? (
          <section className="card p-5 space-y-5">
            <div><h2 className="font-semibold">{STEPS[step - 1]!.label}</h2><p className="mt-1 text-sm text-gray-600">กรอกเฉพาะข้อมูลที่ใช้กับเอกสารที่เลือกไว้</p></div>
            {pageQuestions.length === 0 ? <EmptyState icon={FileText} title="ไม่มีข้อมูลที่ต้องกรอกในขั้นนี้" detail="ไปขั้นตอนถัดไปเพื่อดำเนินการต่อ" /> : pageQuestions.map((question) => (
              <QuestionField key={question.key} question={question} value={answers[question.key]} fromRegistry={Object.prototype.hasOwnProperty.call(profileData.registry, question.key)} disabled={busy} onChange={(value) => setAnswers((current) => ({ ...current, [question.key]: value }))} />
            ))}
          </section>
        ) : (
          <section className="card p-5 space-y-5">
            <div><h2 className="font-semibold">สรุปก่อนสร้างร่างเอกสาร</h2><p className="mt-1 text-sm text-gray-600">ตรวจข้อมูลให้เรียบร้อย แล้วระบบจะสร้างเอกสารตามรายการนี้</p></div>
            <div className="rounded-lg border border-gray-200 p-4"><h3 className="font-medium">เอกสารที่จะสร้าง</h3><ul className="mt-2 space-y-1 text-sm text-gray-700">{selectedTemplates.map((template) => <li key={template.code}>{template.title}</li>)}</ul></div>
            <div className="rounded-lg border border-gray-200 p-4"><h3 className="font-medium">ข้อมูลที่ตอบ</h3><dl className="mt-2 space-y-2 text-sm">{catalog.questions.filter((question) => profile.selected_codes.some((code) => catalog.templates.find((template) => template.code === code)?.uses_keys.includes(question.key))).map((question) => <div key={question.key} className="grid gap-1 sm:grid-cols-2"><dt className="text-gray-600">{question.label}</dt><dd className="break-words font-medium">{summaryValue(answers[question.key])}</dd></div>)}</dl></div>
            <div><label className="label" htmlFor="effective-from">วันที่มีผลบังคับใช้</label><input id="effective-from" className="input min-h-11 w-full text-base" type="date" value={effectiveFrom} disabled={busy} onChange={(event) => { setEffectiveFrom(event.target.value); writeEffectiveDate(event.target.value); }} /></div>
            <ConsequenceBody intro="เมื่อยืนยัน ระบบจะสร้างร่างเอกสารและเริ่มจัดทำไฟล์ให้" lines={[{ label: 'จำนวนเอกสาร', value: `${selectedTemplates.length} ฉบับ` }, { label: 'วันที่มีผล', value: effectiveFrom || 'ยังไม่ได้ระบุ' }]} effects={['ร่างเอกสารยังไม่มีผลบังคับใช้', 'ต้องตรวจและเผยแพร่ก่อนพนักงานจะเห็นเอกสาร']} />
          </section>
        )}
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-gray-200 bg-white/95 p-3 backdrop-blur md:static md:border-0 md:bg-transparent md:p-0"><div className="mx-auto flex max-w-3xl gap-2"><button className="btn-secondary min-h-11" disabled={busy || step === 1} onClick={() => void moveTo(step - 1)}><ArrowLeft className="h-4 w-4" /> ย้อนกลับ</button>{step < 6 ? <button className="btn-cta min-h-11 flex-1" disabled={busy} onClick={() => void moveTo(step + 1)}><Save className="h-4 w-4" /> {busy ? 'กำลังบันทึก' : 'บันทึกและไปต่อ'} <ArrowRight className="h-4 w-4" /></button> : <button className="btn-cta min-h-11 flex-1" disabled={busy} onClick={() => void createBatch()}><FileText className="h-4 w-4" /> {busy ? 'กำลังสร้างเอกสาร' : 'สร้างร่างเอกสาร'}</button>}</div></div>
      </div>
    </AdminLayout>
  );
}
